import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../api";
import { tagClasses } from "../lib/tags";
import { formatMoney } from "../lib/format";
import { MONTHS_SHORT } from "../lib/months";
import { useSettings } from "../SettingsContext";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const parts = iso.split("-");
  return parts.length === 3 ? `${parts[1]}/${parts[2]}` : iso;
};

const SupplierOverview = ({ accountId = null, accountName = null, initialYear = null, onClose }) => {
  const { settings } = useSettings();
  const [year, setYear] = useState(initialYear != null ? String(initialYear) : "all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (accountId) params.account_id = accountId;
        if (year !== "all") params.year = parseInt(year, 10);
        const res = await axios.get(`${API}/overview/suppliers`, { params });
        if (!cancelled) setData(res.data);
      } catch (e) {
        console.error("Failed to load overview:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accountId, year]);

  const toggle = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const categories = data?.categories || [];

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="supplier-overview-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Spending Overview</h2>
            <p className="text-xs text-gray-500" data-testid="overview-scope">
              {accountName ? accountName : "All accounts"} · grouped by tag and supplier
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              data-testid="overview-year-filter"
              className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="all">All years</option>
              {(data?.years || []).map((y) => (
                <option key={y} value={String(y)}>
                  {y}
                </option>
              ))}
            </select>
            <button
              onClick={onClose}
              data-testid="overview-close"
              className="text-gray-400 hover:text-gray-600 text-xl leading-none"
            >
              ×
            </button>
          </div>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-5">
          {loading ? (
            <p className="text-sm text-gray-500 text-center py-8">Loading…</p>
          ) : categories.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8" data-testid="overview-empty">
              No tagged transactions{year !== "all" ? ` for ${year}` : ""} yet. Tag transactions in the
              year table to see them grouped here.
            </p>
          ) : (
            categories.map((cat) => (
              <section key={cat.tag} data-testid={`overview-category-${cat.tag}`}>
                <div className="flex items-center justify-between mb-2">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium border ${tagClasses(cat.tag, settings.tags)}`}
                  >
                    {cat.tag}
                  </span>
                  <span className="text-sm font-semibold text-gray-900" data-testid={`overview-category-total-${cat.tag}`}>
                    {formatMoney(cat.total, settings)}
                    <span className="ml-2 text-xs font-normal text-gray-500">
                      {cat.count} transaction{cat.count === 1 ? "" : "s"}
                    </span>
                  </span>
                </div>
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden">
                  {cat.suppliers.map((sup) => {
                    const key = `${cat.tag}::${sup.name}`;
                    const open = expanded.has(key);
                    return (
                      <div key={key}>
                        <button
                          onClick={() => toggle(key)}
                          data-testid={`overview-supplier-${cat.tag}-${sup.name}`}
                          className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-gray-50"
                        >
                          <span className="flex items-center gap-2 min-w-0">
                            <svg
                              className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <span className={`text-sm truncate ${sup.name === "(no supplier)" ? "text-gray-400 italic" : "text-gray-900"}`}>
                              {sup.name}
                            </span>
                            {sup.is_supplier && (
                              <span className="text-[10px] font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-1.5 py-0.5 flex-shrink-0">
                                supplier
                              </span>
                            )}
                          </span>
                          <span className="text-sm font-medium text-gray-900 whitespace-nowrap ml-3">
                            {formatMoney(sup.total, settings)}
                            <span className="ml-2 text-xs font-normal text-gray-500">×{sup.count}</span>
                          </span>
                        </button>
                        {open && (
                          <div className="bg-gray-50 px-3 pb-2" data-testid={`overview-supplier-txs-${cat.tag}-${sup.name}`}>
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-left text-gray-400 uppercase tracking-wide">
                                  <th className="py-1.5 pr-2 font-medium w-16">Date</th>
                                  <th className="py-1.5 pr-2 font-medium w-20">Month</th>
                                  {!accountId && <th className="py-1.5 pr-2 font-medium">Account</th>}
                                  <th className="py-1.5 pr-2 font-medium">Description</th>
                                  <th className="py-1.5 font-medium text-right w-24">Amount</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200/60">
                                {sup.transactions.map((tx, i) => (
                                  <tr key={i} className="text-gray-700">
                                    <td className="py-1.5 pr-2 whitespace-nowrap">{fmtDate(tx.date)}</td>
                                    <td className="py-1.5 pr-2 whitespace-nowrap uppercase text-gray-500">
                                      {MONTHS_SHORT[tx.month - 1]} {tx.year}
                                    </td>
                                    {!accountId && (
                                      <td className="py-1.5 pr-2 truncate max-w-[10rem]">{tx.account_name}</td>
                                    )}
                                    <td className="py-1.5 pr-2 truncate max-w-[14rem]">{tx.description}</td>
                                    <td className={`py-1.5 text-right whitespace-nowrap font-medium ${tx.refund ? "text-red-600" : ""}`} title={tx.refund ? "Refund — excluded from totals" : undefined}>
                                      {tx.amount != null ? formatMoney(tx.amount, settings) : "—"}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default SupplierOverview;
